import { Injectable } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { BusinessUsersDispatch } from '@state/business-users/business-users.dispatch';
import { GoogleEncryptionService } from '../../api-repos/google-encryption/google-encryption.api.service';
import { SnackbarService } from '../ui/snackbar.service';
import LogRocket from 'logrocket';

export interface ICloserSyncPinForm {
	accessPin: FormControl<string>;
	confirmAccessPin: FormControl<string>;
}

export const CLOSER_SYNC_DESCRIPTION =
	'Introducing Sync by Closer, a cutting-edge mortgage processing system for digitizing mortgage instructions and processing various lender forms.';

@Injectable({
	providedIn: 'root',
})
export class CloserSyncService {
	constructor(
		private snackbarService: SnackbarService,
		private googleEncryptionService: GoogleEncryptionService,
		private businessUsersDispatch: BusinessUsersDispatch,
		private fb: FormBuilder,
	) {}

	public CLOSER_SYNC_DESCRIPTION = CLOSER_SYNC_DESCRIPTION;

	public async savePin(form: FormGroup<ICloserSyncPinForm>): Promise<boolean> {
		if (form.invalid) {
			this.snackbarService.openSnackBar({
				text: 'Please fill out all required fields',
				type: 'error',
			});
			return;
		}

		try {
			const pin = form.value.accessPin;
			const encryptedPin = await this.googleEncryptionService
				.encrypt(pin)
				.toPromise();
			this.businessUsersDispatch.SetAccessPin(encryptedPin);
			this.snackbarService.openSnackBar({
				text: 'Access PIN saved',
				type: 'success',
			});
			form.reset();
			return true;
		} catch {
			this.snackbarService.openSnackBar({
				text: 'Error saving access PIN',
				type: 'error',
			});
			return false;
		}
	}

	public saveSignature(signature: string) {
		this.businessUsersDispatch.SetSignature(signature);
		this.snackbarService.openSnackBar({
			text: 'Signature saved',
			type: 'success',
		});
	}

	public saveLenderConsent() {
		this.businessUsersDispatch.SetLenderConsent(true);
		this.snackbarService.openSnackBar({
			text: 'Lender consent saved',
			type: 'success',
		});
	}

	public createPinForm(): FormGroup<ICloserSyncPinForm> {
		return this.fb.group(
			{
				accessPin: ['', []],
				confirmAccessPin: ['', []],
			},
			{ validators: this.matchPins },
		);
	}

	public async checkPin(pin: string, encryptedPin: string): Promise<boolean> {
		try {
			if (!pin || pin.length === 0) return false;

			return await this.googleEncryptionService
				.validate(pin, encryptedPin)
				.toPromise();
		} catch (error) {
			console.log('Error checking pin', error);
			LogRocket.captureException(error);
			return false;
		}
	}

	private matchPins(group: FormGroup<ICloserSyncPinForm>) {
		const pin = group.get('accessPin').value;
		const confirmPin = group.get('confirmAccessPin').value;

		group.controls.confirmAccessPin.setErrors(
			pin === confirmPin ? null : { notMatching: true },
		);
	}
}
